import React, {useState, useEffect} from "react";
import { Textbox, Image } from "fabric";
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton} from '@mui/material';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';

const textColorOptions = [
    { label: 'Czarny', value: '#000' },
    { label: 'Szary', value: '#707070' },
    { label: 'Biały', value: '#fff' },
    { label: 'Złoty', value: '#C8A044' },
    { label: 'Granatowy', value: '#1C3F58' }
];

function Settings({canvas, isOpen = true, isMobile = false, onClose}) {
    const [selectedObject, setSelectedObject] = useState(null);
    const [width, setWidth] = useState("");
    const [height, setHeight] = useState("");
    const [fontSize, setFontSize] = useState("");
    const [fontFamily, setFontFamily] = useState("");
    const [color, setColor] = useState("");

    useEffect(()=> {
        if(canvas) {
            canvas.on("selection:created", (event) => {
                handleObjectSelection(event.selected[0]);
            });

            canvas.on("selection:updated", (event) => {
                handleObjectSelection(event.selected[0]);
            });

            canvas.on("selection:cleared", () => {
                setSelectedObject(null);
                clearSettings();
            });

            canvas.on("object:modified", (event) => {
                handleObjectSelection(event.target);
            });

            canvas.on("object:scaling", (event) => {
                handleObjectSelection(event.target);
            });
        }
        
    }, [canvas])

    const handleObjectSelection = (object) => {
        setSelectedObject(object);
        if(object.type === "textbox") {
            setFontSize(object.fontSize);
            setFontFamily(object.fontFamily);
            setColor(object.fill || "#000");
        }
        else if(object.type === "image") {
            setWidth(Math.round(object.width * object.scaleX));
            setHeight(Math.round(object.height * object.scaleY));
        }
    }

    const clearSettings = () => {
        setWidth("");
        setHeight("");
        setFontSize("");
        setFontFamily("");
        setColor("");
    }

    const handleWidthChange = (e) => {
        const value = e.target.value.replace(/,/g, "");
        const intValue = parseInt(value, 10);

        setWidth(intValue);
        if(selectedObject.type === "textbox") {
            if(intValue >=0) {
                selectedObject.set({
                    width: intValue / selectedObject.scaleX,
                    scaleX: 1, 
                    scaleY: 1
                });
                selectedObject.initDimensions();
                selectedObject.setCoords();
                canvas.renderAll();
            }
        }
        else if(selectedObject.type === "image") {
            if(intValue >=0) {
                const newScaleX = intValue / selectedObject.width;
                selectedObject.set({ scaleX: newScaleX });
                canvas.renderAll();
            }
        }
    }

    const handleHeightChange = (e) => {
        const value = e.target.value.replace(/,/g, "");
        const intValue = parseInt(value, 10);

        setHeight(intValue);
        if(selectedObject.type === "textbox") {
            if(intValue >=0) {
                selectedObject.set({height: intValue / selectedObject.scaleY});
                canvas.renderAll();
            }
        }
        else if(selectedObject.type === "image") {
            if(intValue >=0) {
                const newScaleY = intValue / selectedObject.height;
                selectedObject.set({ scaleY: newScaleY });
                canvas.renderAll();
            }
        }
    }

    const handleFontSizeChange = (e) => {
        const value = e.target.value.replace(/,/g, "");
        const intValue = parseInt(value, 10);

        setFontSize(intValue);
        if(selectedObject && intValue >=0) {
            selectedObject.set({fontSize: intValue});
            canvas.renderAll();
        }
    }

    const handleItalicChange = () => {
        if (!selectedObject || selectedObject.type !== "textbox") return;
    
        const selectionStart = selectedObject.selectionStart;
        const selectionEnd = selectedObject.selectionEnd;
    
        if (selectionStart === selectionEnd) {
            const currentStyle = selectedObject.fontStyle;
            const newValue = currentStyle === 'italic' ? 'normal' : 'italic';
            selectedObject.set({ fontStyle: newValue });
            for (let i = 0; i < selectedObject.text.length; i++) {
                selectedObject.setSelectionStyles({ fontStyle: newValue }, i, i + 1);
            }
        } else {
            const styles = selectedObject.getSelectionStyles(selectionStart, selectionEnd);
            const currentStyle = styles.length > 0 && styles[0].fontStyle === 'italic' ? 'italic' : 'normal';
            const newStyle = currentStyle === 'italic' ? 'normal' : 'italic';
    
            for (let i = selectionStart; i < selectionEnd; i++) {
                selectedObject.setSelectionStyles({ fontStyle: newStyle }, i, i + 1);
            }
        }
    
        // Wymuszenie odświeżania
        selectedObject.set({ dirty: true });
        canvas.renderAll();
    };

    const handleUnderlineChange = () => {
        if (!selectedObject || selectedObject.type !== "textbox") return;
    
        const selectionStart = selectedObject.selectionStart;
        const selectionEnd = selectedObject.selectionEnd;
    
        if (selectionStart === selectionEnd) {
            const newValue = !selectedObject.underline;
            selectedObject.set({ underline: newValue });
            for (let i = 0; i < selectedObject.text.length; i++) {
                selectedObject.setSelectionStyles({ underline: newValue }, i, i + 1);
            }
        } else {
            let allUnderlined = true;
            let allNotUnderlined = true;
            const styles = selectedObject.getSelectionStyles(selectionStart, selectionEnd);
    
            for (let style of styles) {
                if (!style.underline) allUnderlined = false;
                if (style.underline) allNotUnderlined = false;
            }
    
            const newValue = allUnderlined || allNotUnderlined ? !styles[0].underline : true;
    
            for (let i = selectionStart; i < selectionEnd; i++) {
                selectedObject.setSelectionStyles({ underline: newValue }, i, i + 1);
            }
        }
        selectedObject.set({ dirty: true });
        canvas.renderAll();
    };

    const handleFontFamilyChange = (e) => {
        const newFontFamily = e.target.value;

        setFontFamily(newFontFamily);
        if(selectedObject && newFontFamily) {
            selectedObject.set({fontFamily: newFontFamily});
            canvas.renderAll();
        }
    }

    const handleTextAlignChange = (value) => {
        if(selectedObject && value) {
            selectedObject.set({textAlign: value});
            canvas.renderAll();
        }
    }

    const handleRemoveSelected = () => {
        const activeObject = canvas.getActiveObject();
        if (activeObject) {
            canvas.remove(activeObject);
            canvas.discardActiveObject();
            canvas.requestRenderAll();
        }
    };

    const handleAddText = () => {
        const newText = new Textbox("Nowy napis", {
            left: 50,
            top: 330,
            fontSize: 50,
            textAlign: 'center',
            width: 300,
            fontWeight: 300,
            scaleX: 1,
            scaleY: 1,
            fixedWidth: true,
        });

        canvas.add(newText);
    }

    const handleAddImage = (event) => {
        const file = event.target.files[0];
        if (!file) return;
    
        const url = URL.createObjectURL(file);
        const imageElement = document.createElement("img");
        imageElement.src = url;
        imageElement.crossOrigin = "anonymous";
    
        // 🔥 Poprawiony event listener:
        imageElement.onload = () => {
            const imageWidth = imageElement.width;
            const imageHeight = imageElement.height;

            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;

            const scale = Math.min(
                canvasWidth / imageWidth,
                canvasHeight / imageHeight
            )
            
            const fabricImage = new Image(imageElement, {
                top: 0,
                left: 0,
                scaleX: scale,
                scaleY: scale,
            });
    
            canvas.add(fabricImage);
            canvas.renderAll();
        };
    };

    const handleBoldSelection = () => {
        if (!selectedObject || selectedObject.type !== "textbox") return;
    
        const selectionStart = selectedObject.selectionStart;
        const selectionEnd = selectedObject.selectionEnd;
    
        if (selectionStart === selectionEnd) {
            const currentWeight = selectedObject.fontWeight;
            const newWeight = currentWeight === 'bold' ? 'normal' : 'bold';
            selectedObject.set({ fontWeight: newWeight });
            for (let i = 0; i < selectedObject.text.length; i++) {
                selectedObject.setSelectionStyles({ fontWeight: newWeight }, i, i + 1);
            }
        } else {
            const styles = selectedObject.getSelectionStyles(selectionStart, selectionEnd);
            const currentWeight = styles.length > 0 && styles[0].fontWeight === 'bold' ? 'bold' : 'normal';
            const newWeight = currentWeight === 'bold' ? 'normal' : 'bold';
    
            for (let i = selectionStart; i < selectionEnd; i++) {
                selectedObject.setSelectionStyles({ fontWeight: newWeight }, i, i + 1);
            }
        }
    
        //wymuszenie odświeżania
        selectedObject.set({ dirty: true });
        canvas.renderAll();
    };

    const updateTextColor = (newColor) => {
        setColor(newColor);
        if(selectedObject && selectedObject.type === "textbox") {
            selectedObject.set({ fill: newColor });
            canvas.renderAll();
        }
    }
    
    
    

    return (
    <div className={`settingsPanel${isMobile ? ' mobile' : ''}${isMobile && isOpen ? ' open' : ''}`}>
    {isMobile && (
        <button className="customBtn mobileCloseBtn" onClick={onClose}>Zamknij</button>
    )}
    <h2 className="settingsPanelTitle">Edycja</h2>
    <button className="customBtn" onClick={handleAddText}>Dodaj tekst</button>
    <input type="file" accept="image/*" id="fileInput" className="inputFile" onChange={handleAddImage} />
    <label htmlFor="fileInput"  className="customBtn secondary customBtn--outline">Dodaj obraz</label>
    {!selectedObject && (
        <p className="settingsHint">
            Kliknij tekst lub krzyż na podglądzie, aby zmienić czcionkę, rozmiar i kolor.
            Dwuklik na tekście pozwala zmienić jego treść.
        </p>
    )}
    {selectedObject && <span className="settingsDivider" aria-hidden="true" />}
    {selectedObject && selectedObject.type === "textbox" && (
        <>
            <label htmlFor="demoFontSize">Rozmiar czcionki (px):</label>
            <input type="number" id="demoFontSize" min="1" value={fontSize} onChange={handleFontSizeChange}/>
            <label htmlFor="fontFamily">Czcionka:</label>
            <select name="fontFamily" id="fontFamily" onChange={handleFontFamilyChange} value={fontFamily} className="selectFont">
                <option value="Arial" style={{fontFamily: 'Arial'}}>Arial</option>
                <option value="Helvetica" style={{fontFamily: 'Helvetica'}}>Helvetica</option>
                <option value="Times New Roman" style={{fontFamily: 'Times New Roman'}}>Times New Roman</option>
                <option value="Courier New" style={{fontFamily: 'Courier New'}}>Courier New</option>
                <option value="Bitter" style={{fontFamily: "Bitter"}}>Bitter</option>
                <option value="DM Serif Display" style={{fontFamily: 'DM Serif Display'}}>DM Serif Display</option>
                <option value="Verdana" style={{fontFamily: 'Verdana'}}>Verdana</option>
                <option value="Georgia" style={{fontFamily: 'Georgia'}}>Georgia</option>
                <option value="monospace" style={{fontFamily: 'monospace'}}>Maszynowa</option>
                <option value="cursive" style={{fontFamily: 'cursive'}}>Odręczna</option>
                <option value="fantasy" style={{fontFamily: 'fantasy'}}>Ozdobna</option>
            </select>
            <span className="settingsLabel">Kolor tekstu:</span>
            <div className="colorPickerGrid" role="radiogroup" aria-label="Kolor tekstu">
                {textColorOptions.map(option => (
                    <button
                        key={option.value}
                        type="button"
                        className={`colorPickerOption${color === option.value ? ' active' : ''}`}
                        onClick={() => updateTextColor(option.value)}
                        aria-pressed={color === option.value}
                    >
                        <span
                            className="colorPickerSwatch"
                            style={{ backgroundColor: option.value }}
                        />
                        <span>{option.label}</span>
                    </button>
                ))}
            </div>
            <div className="btnsWrapperThree">
                {/* Przyciski dla textAlign */}
                <IconButton
                color="primary"
                size="large"
                onClick={() => handleTextAlignChange('left')}
                className="btnWrapperDel"
                aria-label="Wyrównaj do lewej"
                title="Wyrównaj do lewej"
                >
                <FormatAlignLeftIcon fontSize="inherit" className="icon"/>
                </IconButton>
                <IconButton
                color="primary"
                size="large"
                onClick={() => handleTextAlignChange('center')}
                className="btnWrapperDel"
                aria-label="Wyśrodkuj"
                title="Wyśrodkuj"
                >
                <FormatAlignCenterIcon fontSize="inherit" className="icon"/>
                </IconButton>
                <IconButton
                color="primary"
                size="large"
                onClick={() => handleTextAlignChange('right')}
                className="btnWrapperDel"
                aria-label="Wyrównaj do prawej"
                title="Wyrównaj do prawej"
                >
                <FormatAlignRightIcon fontSize="inherit" className="icon"/>
                </IconButton>
                {/* Przyciski dla pogrubienia, italic, underline */}
                <IconButton className="btnWrapperDel" color="primary" onClick={handleBoldSelection} aria-label="Pogrubienie" title="Pogrubienie">
                    <FormatBoldIcon fontSize="inherit" className="icon"/>
                </IconButton>
                <IconButton className="btnWrapperDel" color="primary" onClick={handleItalicChange} aria-label="Kursywa" title="Kursywa">
                    <FormatItalicIcon fontSize="inherit" className="icon"/>
                </IconButton>
                <IconButton className="btnWrapperDel" color="primary" onClick={handleUnderlineChange} aria-label="Podkreślenie" title="Podkreślenie">
                    <FormatUnderlinedIcon fontSize="inherit" className="icon"/>
                </IconButton>
            </div>
            <IconButton
              color="primary"
              size="large"
              onClick={handleRemoveSelected}
              className="btnWrapperDel"
              aria-label="Usuń zaznaczony element"
              title="Usuń zaznaczony element"
            >
              <DeleteIcon fontSize="inherit" className="icon"/>
            </IconButton>
        </>
    )}
    {selectedObject && selectedObject.type === "image" && (
        <>
        <label htmlFor="demoImageWidth">Szerokość (px):</label>
        <input type="number" id="demoImageWidth" min="1" value={width} onChange={handleWidthChange}/>
        <label htmlFor="demoImageHeight">Wysokość (px):</label>
        <input type="number" id="demoImageHeight" min="1" value={height} onChange={handleHeightChange}/>
            <IconButton
              color="primary"
              size="large"
              onClick={handleRemoveSelected}
              className="btnWrapperDel"
              aria-label="Usuń zaznaczony element"
              title="Usuń zaznaczony element"
            >
              <DeleteIcon fontSize="inherit" className="icon"/>
            </IconButton>
        </>
    )}
    </div>
    );
}

export default Settings;
